import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch, apiDownload } from '../../../lib/api';
import { API_DOCS } from './ImportListinoDettaglioPage.api';

// ── Interfacce ────────────────────────────────────────────────────────────────

interface ImportListino {
  id: number;
  societaId: number;
  etichetta: string;
  note: string | null;
  stato: 'IN_CORSO' | 'COMPLETATO' | 'COMPLETATO_CON_ERRORI' | 'ERRORE';
  importatoAt: string;
  totaleRighe: number;
  righeCreate: number;
  righeAggiornate: number;
  righeErrore: number;
  gcsPathExcel: string | null;
  gcsPathLog: string | null;
  logTesto: string | null;
  fornitore: { id: number; ragioneSociale: string | null; nome: string | null } | null;
  utente: { id: number; nome: string; email: string } | null;
}

// ── Helper badge ──────────────────────────────────────────────────────────────

const badgeStato = (stato: string) => {
  const map: Record<string, string> = {
    COMPLETATO: 'bop-badge-success',
    COMPLETATO_CON_ERRORI: 'bop-badge-warning',
    ERRORE: 'bop-badge-danger',
    IN_CORSO: 'bop-badge-neutral',
  };
  const labels: Record<string, string> = {
    COMPLETATO: 'Completato',
    COMPLETATO_CON_ERRORI: 'Con errori',
    ERRORE: 'Errore',
    IN_CORSO: 'In corso',
  };
  return (
    <span className={`bop-badge ${map[stato] || 'bop-badge-neutral'}`}>
      {labels[stato] || stato}
    </span>
  );
};

// ── Componente principale ─────────────────────────────────────────────────────

export default function ImportListinoDettaglioPage() {
  const { id } = useParams<{ id: string }>();
  const [imp, setImp]       = useState<ImportListino | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  const caricaDettaglio = async () => {
    try {
      setLoading(true);
      // @api GET /api/anagrafiche/listini/:id -> {"success":true,"data":"ImportListino"}
      const res = await apiFetch<{ success: boolean; data: ImportListino }>(
        `/api/anagrafiche/listini/${id}`
      );
      setImp(res.data);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) caricaDettaglio();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScaricaExcel = async () => {
    try {
      // @api GET /api/anagrafiche/listini/:id/file-excel -> (binary xlsx)
      await apiDownload(
        `/api/anagrafiche/listini/${id}/file-excel`,
        `listino_import_${id}.xlsx`
      );
    } catch (err: any) {
      alert('Errore download Excel: ' + err.message);
    }
  };

  const handleScaricaLog = async () => {
    try {
      // @api GET /api/anagrafiche/listini/:id/file-log -> (binary txt)
      await apiDownload(
        `/api/anagrafiche/listini/${id}/file-log`,
        `log_import_${id}.txt`
      );
    } catch (err: any) {
      alert('Errore download log: ' + err.message);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="import-dettaglio-page">
          <div className="bop-empty-state">Caricamento...</div>
        </div>
      </Layout>
    );
  }

  if (error || !imp) {
    return (
      <Layout>
        <div className="import-dettaglio-page">
          <div className="bop-empty-state bop-text-danger">
            {error || 'Import non trovato'}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="import-dettaglio-page">
        <BopBackLink to="/anagrafiche/articoli/listini" label="Torna ai listini" />

        {/* Header */}
        <div className="bop-card import-dettaglio-header">
          <div className="import-dettaglio-header-main">
            <h1 className="bop-page-title">{imp.etichetta}</h1>
            <div className="import-dettaglio-header-sub">
              {badgeStato(imp.stato)}
              <span className="bop-text-muted">
                {new Date(imp.importatoAt).toLocaleString('it-IT')}
              </span>
            </div>
          </div>
          <div className="import-dettaglio-meta">
            <div>
              <strong>Fornitore:</strong>{' '}
              {imp.fornitore?.ragioneSociale || imp.fornitore?.nome || '-'}
            </div>
            <div>
              <strong>Utente:</strong> {imp.utente?.nome || '-'}
            </div>
          </div>
        </div>

        {/* Riepilogo numerico */}
        <div className="bop-card">
          <h2 className="listini-sezione-titolo">Riepilogo</h2>
          <div className="listini-contatori">
            {[
              { label: 'Totale',     valore: imp.totaleRighe },
              { label: 'Creati',     valore: imp.righeCreate },
              { label: 'Aggiornati', valore: imp.righeAggiornate },
              { label: 'Errori',     valore: imp.righeErrore },
            ].map(({ label, valore }) => (
              <div key={label} className="listini-contatore">
                <span className="listini-contatore-valore">{valore}</span>
                <span className="listini-contatore-label">{label}</span>
              </div>
            ))}
          </div>
          <div className="import-dettaglio-link-righe">
            <button
              className="bop-btn-secondary"
              onClick={() => window.open(`/anagrafiche/articoli/listini-fornitori/${imp.id}`, '_blank')}
            >
              📋 Apri articoli importati
            </button>
          </div>
        </div>

        {/* Note */}
        {imp.note && (
          <div className="bop-card">
            <h2 className="listini-sezione-titolo">Note</h2>
            <p className="bop-text-muted">{imp.note}</p>
          </div>
        )}

        {/* Log importazione — sempre visibile se presente nel DB */}
        {!imp.logTesto && imp.stato !== 'IN_CORSO' && (
          <div className="bop-card">
            <h2 className="listini-sezione-titolo">Log importazione</h2>
            <p className="bop-text-muted">
              Log non disponibile per questo import (elaborato prima dell’aggiornamento del sistema).
            </p>
          </div>
        )}
        {imp.logTesto && (
          <div className="bop-card">
            <div className="import-dettaglio-log-header">
              <h2 className="listini-sezione-titolo">Log importazione</h2>
              <button
                className="bop-btn-secondary"
                onClick={() => {
                  const blob = new Blob([imp.logTesto!], { type: 'text/plain;charset=utf-8' });
                  const url  = URL.createObjectURL(blob);
                  const a    = document.createElement('a');
                  a.href     = url;
                  a.download = `log_import_${imp.id}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
              >
                ⬇ Scarica log (.txt)
              </button>
            </div>
            <pre className="import-dettaglio-log-pre">{imp.logTesto}</pre>
          </div>
        )}

        {/* File allegati su GCS — solo se disponibili */}
        {(imp.gcsPathExcel || imp.gcsPathLog) && (
          <div className="bop-card">
            <h2 className="listini-sezione-titolo">File Allegati</h2>
            <div className="import-dettaglio-file-azioni">
              {imp.gcsPathExcel && (
                <button className="bop-btn-secondary" onClick={handleScaricaExcel}>
                  ⬇ Excel originale
                </button>
              )}
              {imp.gcsPathLog && (
                <button className="bop-btn-secondary" onClick={handleScaricaLog}>
                  ⬇ Log importazione (GCS)
                </button>
              )}
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
